/**
 * Zoom en la galería PDP (sin modal): lupa en escritorio, pellizco y doble toque en móvil.
 */

const LENS_SIZE = 132;
const LENS_ZOOM = 2.4;
const PINCH_MAX = 3;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_PX = 40;

/** @type {WeakMap<HTMLElement, { scale: number, tx: number, ty: number, abort: AbortController }>} */
const stateMap = new WeakMap();

function isCoarsePointer() {
  return window.matchMedia('(pointer: coarse)').matches;
}

function getHiResSrc(img) {
  if (!(img instanceof HTMLImageElement)) return '';
  return img.getAttribute('data_max_resolution') || img.currentSrc || img.src;
}

/** @param {HTMLImageElement} img */
function getDisplayedImageRect(img) {
  return img.getBoundingClientRect();
}

function resetHost(host) {
  const state = stateMap.get(host);
  if (!state) return;
  state.scale = 1;
  state.tx = 0;
  state.ty = 0;
  host.classList.remove('is-pinch-zoomed', 'is-lens-active');
  const img = host.querySelector('img.product-media__image');
  const lens = host.querySelector('.optima-zoom-lens');
  if (img instanceof HTMLImageElement) {
    img.style.transform = '';
    img.style.transformOrigin = '';
  }
  if (lens instanceof HTMLElement) lens.hidden = true;
}

function applyPinchTransform(host) {
  const state = stateMap.get(host);
  const img = host.querySelector('img.product-media__image');
  if (!state || !(img instanceof HTMLImageElement)) return;

  if (state.scale <= 1.01) {
    img.style.transform = '';
    img.style.transformOrigin = 'center center';
    host.classList.remove('is-pinch-zoomed');
    return;
  }

  host.classList.add('is-pinch-zoomed');
  img.style.transformOrigin = 'center center';
  img.style.transform = `scale(${state.scale}) translate(${state.tx}px, ${state.ty}px)`;
}

function constrainPinch(host) {
  const state = stateMap.get(host);
  const img = host.querySelector('img.product-media__image');
  if (!state || !(img instanceof HTMLImageElement)) return;

  if (state.scale < 1) state.scale = 1;
  if (state.scale > PINCH_MAX) state.scale = PINCH_MAX;

  if (state.scale <= 1) {
    state.tx = 0;
    state.ty = 0;
    return;
  }

  const hostRect = host.getBoundingClientRect();
  const content = getDisplayedImageRect(img);
  const scaledW = content.width * state.scale;
  const scaledH = content.height * state.scale;
  const maxX = Math.max(0, (scaledW - content.width) / 2 / state.scale);
  const maxY = Math.max(0, (scaledH - content.height) / 2 / state.scale);
  state.tx = Math.min(maxX, Math.max(-maxX, state.tx));
  state.ty = Math.min(maxY, Math.max(-maxY, state.ty));
}

function setupLens(host, img, lens, signal) {
  if (isCoarsePointer()) return;

  const move = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerType === 'touch') return;

    const hostRect = host.getBoundingClientRect();
    const content = getDisplayedImageRect(img);
    const x = event.clientX;
    const y = event.clientY;

    if (
      content.width < 2 ||
      content.height < 2 ||
      x < content.left ||
      x > content.left + content.width ||
      y < content.top ||
      y > content.top + content.height
    ) {
      lens.hidden = true;
      host.classList.remove('is-lens-active');
      return;
    }

    const relX = x - content.left;
    const relY = y - content.top;
    const lensHalf = LENS_SIZE / 2;
    const offsetX = content.left - hostRect.left;
    const offsetY = content.top - hostRect.top;

    let lensLeft = offsetX + relX - lensHalf;
    let lensTop = offsetY + relY - lensHalf;
    lensLeft = Math.max(0, Math.min(hostRect.width - LENS_SIZE, lensLeft));
    lensTop = Math.max(0, Math.min(hostRect.height - LENS_SIZE, lensTop));

    const hiRes = getHiResSrc(img);
    const bgW = content.width * LENS_ZOOM;
    const bgH = content.height * LENS_ZOOM;

    lens.style.width = `${LENS_SIZE}px`;
    lens.style.height = `${LENS_SIZE}px`;
    lens.style.left = `${lensLeft}px`;
    lens.style.top = `${lensTop}px`;
    lens.style.backgroundImage = `url("${hiRes}")`;
    lens.style.backgroundSize = `${bgW}px ${bgH}px`;
    lens.style.backgroundPosition = `${-(relX * LENS_ZOOM - lensHalf)}px ${-(relY * LENS_ZOOM - lensHalf)}px`;

    lens.hidden = false;
    host.classList.add('is-lens-active');
  };

  host.addEventListener(
    'pointerenter',
    (e) => {
      if (e.pointerType === 'touch') return;
      host.classList.add('is-lens-active');
    },
    { signal }
  );
  host.addEventListener('pointerleave', () => {
    lens.hidden = true;
    host.classList.remove('is-lens-active');
  }, { signal });
  host.addEventListener('pointermove', move, { signal });
}

function setupPinch(host, img, signal) {
  const state = stateMap.get(host);
  if (!state) return;

  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOriginTx = 0;
  let dragOriginTy = 0;
  let dragging = false;
  let lastTap = 0;
  let lastTapX = 0;
  let lastTapY = 0;

  const onTouchStart = (event) => {
    if (!(event instanceof TouchEvent)) return;
    if (event.touches.length === 2) {
      const [a, b] = event.touches;
      pinchStartDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchStartScale = state.scale;
      dragging = false;
      event.preventDefault();
    } else if (event.touches.length === 1) {
      const t = event.touches[0];
      dragStartX = t.clientX;
      dragStartY = t.clientY;
      dragOriginTx = state.tx;
      dragOriginTy = state.ty;
      dragging = state.scale > 1.02;

      const now = Date.now();
      if (now - lastTap < DOUBLE_TAP_MS && Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY) < DOUBLE_TAP_PX) {
        if (state.scale > 1.02) {
          state.scale = 1;
          state.tx = 0;
          state.ty = 0;
        } else {
          state.scale = 2;
        }
        constrainPinch(host);
        applyPinchTransform(host);
        lastTap = 0;
        event.preventDefault();
        return;
      }
      lastTap = now;
      lastTapX = t.clientX;
      lastTapY = t.clientY;
    }
  };

  const onTouchMove = (event) => {
    if (!(event instanceof TouchEvent)) return;
    if (event.touches.length === 2 && pinchStartDist > 0) {
      const [a, b] = event.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      state.scale = (dist / pinchStartDist) * pinchStartScale;
      constrainPinch(host);
      applyPinchTransform(host);
      event.preventDefault();
    } else if (event.touches.length === 1 && dragging) {
      const t = event.touches[0];
      const dx = (t.clientX - dragStartX) / state.scale;
      const dy = (t.clientY - dragStartY) / state.scale;
      state.tx = dragOriginTx + dx;
      state.ty = dragOriginTy + dy;
      constrainPinch(host);
      applyPinchTransform(host);
      event.preventDefault();
    }
  };

  const onTouchEnd = () => {
    dragging = false;
    pinchStartDist = 0;
  };

  host.addEventListener('touchstart', onTouchStart, { passive: false, signal });
  host.addEventListener('touchmove', onTouchMove, { passive: false, signal });
  host.addEventListener('touchend', onTouchEnd, { signal });
}

function bindZoomHost(host) {
  if (host.dataset.optimaZoomBound === 'true') return;
  const img = host.querySelector('img.product-media__image');
  if (!(img instanceof HTMLImageElement)) return;

  host.dataset.optimaZoomBound = 'true';
  host.classList.add('optima-zoom-host');

  const lens = document.createElement('div');
  lens.className = 'optima-zoom-lens';
  lens.hidden = true;
  lens.setAttribute('aria-hidden', 'true');
  host.appendChild(lens);

  const abort = new AbortController();
  stateMap.set(host, { scale: 1, tx: 0, ty: 0, abort });

  setupLens(host, img, lens, abort.signal);
  setupPinch(host, img, abort.signal);

  const zoomBtn = host.querySelector('.product-media-container__zoom-button');
  if (zoomBtn instanceof HTMLButtonElement) {
    zoomBtn.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        const state = stateMap.get(host);
        if (!state) return;
        if (isCoarsePointer()) {
          state.scale = state.scale > 1.02 ? 1 : 2;
          state.tx = 0;
          state.ty = 0;
          constrainPinch(host);
          applyPinchTransform(host);
        } else {
          host.classList.toggle('is-lens-active');
          if (!host.classList.contains('is-lens-active')) lens.hidden = true;
        }
      },
      { signal: abort.signal }
    );
  }
}

function disableModalZoom(gallery) {
  gallery.querySelectorAll('[on\\:click]').forEach((el) => {
    const action = el.getAttribute('on:click') || '';
    if (action.includes('zoom-dialog') || action.includes('/zoom/')) {
      el.removeAttribute('on:click');
    }
  });
  gallery.querySelectorAll('[on\\:pointerenter]').forEach((el) => {
    const action = el.getAttribute('on:pointerenter') || '';
    if (action.includes('preloadImage')) el.removeAttribute('on:pointerenter');
  });

  gallery.querySelectorAll('slideshow-slide.product-media-container--zoomable').forEach((slide) => {
    slide.addEventListener(
      'click',
      (e) => {
        if (e.target instanceof Element && e.target.closest('.slideshow-control, .product-media-container__zoom-button')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  });
}

function bindGallery(gallery) {
  if (gallery.dataset.optimaZoomGallery === 'true') return;
  gallery.dataset.optimaZoomGallery = 'true';

  disableModalZoom(gallery);

  gallery.querySelectorAll('slideshow-slide.product-media-container--image').forEach((slide) => {
    if (slide instanceof HTMLElement) bindZoomHost(slide);
  });

  gallery.addEventListener('slideshow:select', () => {
    gallery.querySelectorAll('slideshow-slide.product-media-container--image').forEach((slide) => {
      if (slide instanceof HTMLElement) {
        if (slide.getAttribute('aria-hidden') === 'true') resetHost(slide);
        else if (slide.dataset.optimaZoomBound !== 'true') bindZoomHost(slide);
      }
    });
  });
}

function initOptimaProductZoom() {
  document.querySelectorAll('.product-information media-gallery').forEach((gallery) => {
    if (gallery instanceof HTMLElement) bindGallery(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOptimaProductZoom);
} else {
  initOptimaProductZoom();
}

document.addEventListener('shopify:section:load', initOptimaProductZoom);
